/**
 * AttachmentItem Utilities
 * 
 * Utility functions for file type detection, icon selection, and display formatting.
 * Reuses and extends the existing file icon system from MessageAttachment.
 */

import {
  IconFile,
  IconFileTypePdf,
  IconPhoto,
  IconFileText,
  IconPresentation,
  IconTable,
  IconX,
} from '@tabler/icons-react';
import type { FileTypeIconMap, FileTypeCategory } from './types';

// =============================================================================
// FILE TYPE ICON MAPPING
// =============================================================================

/** File type to icon mapping based on MIME types and extensions */
export const FILE_TYPE_ICONS: FileTypeIconMap = {
  // PDF files
  'application/pdf': IconFileTypePdf,
  
  // Document files
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': IconFileText,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': IconPresentation,
  'application/msword': IconFileText,
  'text/plain': IconFileText,
  
  // Spreadsheet files
  'text/csv': IconTable,
  'application/vnd.ms-excel': IconTable,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': IconTable,
  
  // Image files
  'image/jpeg': IconPhoto,
  'image/jpg': IconPhoto,
  'image/png': IconPhoto,
  'image/gif': IconPhoto,
  'image/svg+xml': IconPhoto,
  'image/webp': IconPhoto,
  
  // Default
  'default': IconFile,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get the appropriate icon component for a file type
 */
export const getFileTypeIcon = (file: File): React.ComponentType<any> => {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  
  // Try exact MIME type match first
  if (FILE_TYPE_ICONS[mimeType]) {
    return FILE_TYPE_ICONS[mimeType];
  }
  
  // Try extension-based matching for edge cases
  const extensionMappings: Record<string, keyof typeof FILE_TYPE_ICONS> = {
    'pdf': 'application/pdf',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'doc': 'application/msword',
    'csv': 'text/csv',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',
    'txt': 'text/plain',
  };
  
  const mappedMimeType = extensionMappings[extension];
  if (mappedMimeType && FILE_TYPE_ICONS[mappedMimeType]) {
    return FILE_TYPE_ICONS[mappedMimeType];
  }
  
  // Default to generic file icon
  return FILE_TYPE_ICONS.default;
};

/**
 * Get the appropriate icon component for a file from filename (for uploaded files)
 */
export const getFileTypeIconFromFilename = (filename: string): React.ComponentType<any> => {
  const extension = filename.split('.').pop()?.toLowerCase() || '';
  
  // Extension-based matching for uploaded files
  const extensionMappings: Record<string, keyof typeof FILE_TYPE_ICONS> = {
    'pdf': 'application/pdf',
    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'doc': 'application/msword',
    'csv': 'text/csv',
    'xls': 'application/vnd.ms-excel',
    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'svg': 'image/svg+xml',
    'webp': 'image/webp',
    'txt': 'text/plain',
  };
  
  const mappedMimeType = extensionMappings[extension];
  if (mappedMimeType && FILE_TYPE_ICONS[mappedMimeType]) {
    return FILE_TYPE_ICONS[mappedMimeType];
  }
  
  // Default to generic file icon
  return FILE_TYPE_ICONS.default;
};

/**
 * Get the remove (close) icon for hover state
 */
export const getRemoveIcon = (): React.ComponentType<any> => {
  return IconX;
};

/**
 * Categorize file type for consistent styling
 */
export const getFileTypeCategory = (file: File): FileTypeCategory => {
  const mimeType = file.type.toLowerCase();
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  
  if (mimeType.includes('pdf') || extension === 'pdf') {
    return 'pdf';
  }
  
  if (mimeType.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) {
    return 'image';
  }
  
  if (
    mimeType.includes('spreadsheet') ||
    mimeType.includes('csv') ||
    ['csv', 'xls', 'xlsx'].includes(extension)
  ) {
    return 'spreadsheet';
  }
  
  if (
    mimeType.includes('document') ||
    mimeType.includes('text') ||
    ['doc', 'docx', 'txt'].includes(extension)
  ) {
    return 'document';
  }
  
  return 'other';
};

/**
 * Format file name for display (truncate if too long)
 */
export const formatFileName = (fileName: string, maxLength: number = 25): string => {
  if (fileName.length <= maxLength) {
    return fileName;
  }
  
  const extension = fileName.split('.').pop() || '';
  const nameWithoutExtension = fileName.slice(0, fileName.lastIndexOf('.'));
  const truncatedName = nameWithoutExtension.slice(0, maxLength - extension.length - 4);
  
  return `${truncatedName}...${extension}`;
};